import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  Loader2,
  AlertTriangle,
  Upload,
  Eye,
  ArrowRight,
  CheckCircle2,
  X,
} from 'lucide-react';
import api from '../../lib/api';

export function CustomerImportTool({ onImportComplete }) {
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportFile(file);
    setImportResults(null);
    setPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/customers/import-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportPreview(response.data);
    } catch (error) {
      toast.error('Failed to preview file: ' + (error.response?.data?.detail || error.message));
      setImportFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImportCustomers = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await api.post('/customers/import-accountsiq', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResults(response.data);
      toast.success(`Imported ${response.data.imported} new, updated ${response.data.updated} existing customers`);
      onImportComplete?.();
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportResults(null);
  };

  return (
    <Card className="border-blue-200 bg-blue-50/30" data-testid="customer-import-tool">
      <CardHeader>
        <CardTitle className="text-blue-700 flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Customer Import Tool
        </CardTitle>
        <CardDescription>Import customers from AccountsIQ or Excel files</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* File Upload */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Label htmlFor="import-file" className="text-sm font-medium">Select Excel File (.xlsx)</Label>
            <Input
              id="import-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="mt-2"
              data-testid="import-file-input"
            />
          </div>
          {importFile && (
            <Button variant="ghost" size="sm" onClick={resetImport}>
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Preview Loading */}
        {previewLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Analyzing file...
          </div>
        )}

        {/* Preview Results */}
        {importPreview && !importResults && (
          <div className="space-y-4 p-4 bg-white rounded-lg border">
            <div className="flex items-center justify-between">
              <h4 className="font-medium flex items-center gap-2">
                <Eye className="w-4 h-4" /> Preview
              </h4>
              <span className="text-xs text-muted-foreground">Sheet: {importPreview.selected_sheet}</span>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div className="text-center p-3 bg-gray-50 rounded">
                <p className="text-2xl font-bold">{importPreview.total_rows}</p>
                <p className="text-xs text-muted-foreground">Total Rows</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded">
                <p className="text-2xl font-bold text-green-600">{importPreview.new_customers}</p>
                <p className="text-xs text-muted-foreground">Will Import</p>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded">
                <p className="text-2xl font-bold text-blue-600">{importPreview.existing_customers}</p>
                <p className="text-xs text-muted-foreground">Will Update</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded">
                <p className="text-2xl font-bold text-amber-600">{importPreview.incomplete || 0}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </div>
            </div>

            {Object.keys(importPreview.mapping || {}).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Detected Columns:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(importPreview.mapping).map(([field, col]) => (
                    <span key={field} className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                      {field} → {col}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {importPreview.sample_rows?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Sample Data (first {importPreview.sample_rows.length} rows):</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border p-2 text-left">Name</th>
                        <th className="border p-2 text-left">Code</th>
                        <th className="border p-2 text-left">City</th>
                        <th className="border p-2 text-left">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.sample_rows.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="border p-2">{row.name || '-'}</td>
                          <td className="border p-2">{row.customer_code || '-'}</td>
                          <td className="border p-2">{row.city || '-'}</td>
                          <td className="border p-2 truncate max-w-[200px]">{row.email || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {importPreview.warnings?.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    {importPreview.warnings.map((warning, idx) => (
                      <div key={idx}>
                        <p className="text-sm font-medium text-amber-800">{warning.message}</p>
                        {warning.examples?.length > 0 && (
                          <p className="text-xs text-amber-700 mt-1">
                            Examples: {warning.examples.join(', ')}
                            {warning.count > warning.examples.length && ` ... and ${warning.count - warning.examples.length} more`}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={resetImport}>Cancel</Button>
              <Button
                onClick={handleImportCustomers}
                disabled={importing || importPreview.new_customers + importPreview.existing_customers + (importPreview.incomplete || 0) === 0}
                data-testid="btn-confirm-import"
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                ) : (
                  <><ArrowRight className="w-4 h-4 mr-2" /> Import {importPreview.new_customers + importPreview.existing_customers + (importPreview.incomplete || 0)} Customers</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Import Results */}
        {importResults && (
          <div className="p-4 bg-white rounded-lg border space-y-3">
            <h4 className="font-medium flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-4 h-4" /> Import Complete
            </h4>
            <div className="grid grid-cols-5 gap-2 text-center">
              <div className="p-2 bg-green-50 rounded">
                <p className="text-xl font-bold text-green-600">{importResults.imported}</p>
                <p className="text-xs">New</p>
              </div>
              <div className="p-2 bg-blue-50 rounded">
                <p className="text-xl font-bold text-blue-600">{importResults.updated}</p>
                <p className="text-xs">Updated</p>
              </div>
              <div className="p-2 bg-amber-50 rounded">
                <p className="text-xl font-bold text-amber-600">{importResults.incomplete || 0}</p>
                <p className="text-xs">Pending</p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="text-xl font-bold text-gray-600">{importResults.skipped}</p>
                <p className="text-xs">No Change</p>
              </div>
              <div className="p-2 bg-purple-50 rounded">
                <p className="text-xl font-bold text-purple-600">{importResults.contacts_added || 0}</p>
                <p className="text-xs">Contacts</p>
              </div>
            </div>
            {importResults.incomplete > 0 && (
              <div className="p-2 bg-amber-50 rounded text-sm text-amber-800">
                <strong>{importResults.incomplete}</strong> customers imported to Pending Review - missing required data
              </div>
            )}
            {importResults.errors?.length > 0 && (
              <div className="mt-2">
                <p className="text-sm font-medium text-red-700 mb-1">Errors:</p>
                <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                  {importResults.errors.slice(0, 10).map((err, idx) => (
                    <p key={idx} className="text-red-600">Row {err.row}: {err.customer || ''} - {err.error}</p>
                  ))}
                  {importResults.errors.length > 10 && (
                    <p className="text-muted-foreground">... and {importResults.errors.length - 10} more errors</p>
                  )}
                </div>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={resetImport} className="mt-2">Import Another File</Button>
          </div>
        )}

        {/* Help Text */}
        {!importFile && (
          <div className="text-sm text-muted-foreground space-y-1">
            <p>Supported formats:</p>
            <ul className="list-disc list-inside ml-2 text-xs">
              <li>AccountsIQ Customer Export (.xlsx)</li>
              <li>Standard Excel with columns: Customer Name, Address, City, Email, VAT No</li>
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
